import { useState } from 'react';
import { useWebAuth } from '../../hooks/useWebAuth';
import { usePaddle } from '../../hooks/usePaddle';
import { getGoogleAuthUrl } from '../../api/auth-api';
import { TIER_LIMITS, formatBytes, PRO_MONTHLY_PRICE, PRO_YEARLY_PRICE } from '@reqshare/shared';
import type { BillingInterval } from '@reqshare/shared';
import './PricingPage.scss';

export function PricingPage() {
  const { user, isAuthenticated, loading } = useWebAuth();
  const isPro = user?.subscription?.tier === 'pro';
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('yearly');

  const { openCheckout, isReady } = usePaddle(() => {
    // Refresh page after successful checkout to reload user profile
    window.location.reload();
  });

  const handleGetPro = () => {
    if (!user) return;
    openCheckout(user.id, user.email, billingInterval);
  };

  const handleGoogleSignIn = () => {
    const returnUrl = window.location.pathname;
    window.location.href = getGoogleAuthUrl() + `&returnTo=${encodeURIComponent(returnUrl)}`;
  };

  const isYearly = billingInterval === 'yearly';
  const displayPrice = isYearly ? PRO_YEARLY_PRICE : PRO_MONTHLY_PRICE;
  const displayPeriod = isYearly ? '/year' : '/month';

  return (
    <main className="pricing-page">
      <div className="pricing-page__container">
        <header className="pricing-page__header">
          <h1 className="pricing-page__title">Pricing</h1>
          <p className="pricing-page__subtitle">
            Share HTTP requests with your team. Upgrade for more.
          </p>
        </header>

        <div className="pricing-page__billing-toggle">
          <button
            type="button"
            className={`pricing-page__billing-option${!isYearly ? ' pricing-page__billing-option--active' : ''}`}
            onClick={() => setBillingInterval('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`pricing-page__billing-option${isYearly ? ' pricing-page__billing-option--active' : ''}`}
            onClick={() => setBillingInterval('yearly')}
          >
            Yearly
            <span className="pricing-page__savings-badge">Save 75%</span>
          </button>
        </div>

        <div className="pricing-page__cards">
          <div className="pricing-page__card">
            <div className="pricing-page__card-header">
              <h2 className="pricing-page__plan-name">Free</h2>
              <p className="pricing-page__plan-price">
                $0<span className="pricing-page__plan-period">/month</span>
              </p>
            </div>
            <ul className="pricing-page__features">
              <li>{TIER_LIMITS.free.dailyShares} shares per day</li>
              <li>Up to {formatBytes(TIER_LIMITS.free.maxPayloadSizeBytes)} per request</li>
              <li>All expiration options</li>
              <li>Sensitive header masking</li>
            </ul>
            {!loading && !isAuthenticated && (
              <button
                type="button"
                className="pricing-page__btn pricing-page__btn--secondary"
                onClick={handleGoogleSignIn}
              >
                Sign in with Google
              </button>
            )}
            {!loading && isAuthenticated && !isPro && (
              <span className="pricing-page__current-badge">Current Plan</span>
            )}
          </div>

          <div className="pricing-page__card pricing-page__card--featured">
            <div className="pricing-page__card-header">
              <h2 className="pricing-page__plan-name">Pro</h2>
              <p className="pricing-page__plan-price">
                ${displayPrice}<span className="pricing-page__plan-period">{displayPeriod}</span>
              </p>
            </div>
            <ul className="pricing-page__features">
              <li>Unlimited shares</li>
              <li>Up to {formatBytes(TIER_LIMITS.pro.maxPayloadSizeBytes)} per request</li>
              <li>All expiration options</li>
              <li>Sensitive header masking</li>
              <li>Priority support</li>
            </ul>
            {loading ? null : !isAuthenticated ? (
              <button
                type="button"
                className="pricing-page__btn"
                onClick={handleGoogleSignIn}
              >
                Sign in to upgrade
              </button>
            ) : isPro ? (
              <span className="pricing-page__current-badge">Current Plan</span>
            ) : (
              <button
                type="button"
                className="pricing-page__btn"
                onClick={handleGetPro}
                disabled={!isReady}
              >
                Get Pro
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
